import { useMemo, useRef, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { X } from "lucide-react";
import { api, type ActivityView } from "../api/client";
import { EditableText } from "../components/EditableText";
import { ConfirmButton } from "../components/ConfirmButton";
import { Modal } from "../components/Modal";
import { CardLabels } from "../components/CardLabels";
import { CardFields } from "../components/CardFields";
import { BoardFieldsDialog } from "../components/BoardFieldsDialog";
import { CardAssignees } from "../components/CardAssignees";
import { CardActivity } from "../components/CardActivity";
import { CardAttachments } from "../components/CardAttachments";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { MarkdownField } from "../components/MarkdownField";
import { OverflowMenu } from "../components/OverflowMenu";
import { MoveCardDialog } from "../components/MoveCardDialog";
import { useMe } from "../hooks/useMe";
import { useAttachmentUpload } from "../hooks/useAttachmentUpload";
import { ColorSwatches } from "../components/ColorSwatches";
import { labelColor } from "../util/labelColors";
import { toDateInputValue, fromDateInputValue, formatRelativeTime } from "../util/date";
import { CARD_TITLE_MAX_LENGTH, type CardRelationship } from "@kandoo/shared";

const RELATIONSHIPS: CardRelationship[] = ["subtask", "blocked-by", "related"];

export function CardModal() {
  const { boardId, cardId } = useParams<{ boardId: string; cardId: string }>();
  const navigate = useNavigate();
  // set when a list-drop just created this card — the title (a filename)
  // probably wants replacing, so open it in edit mode
  const location = useLocation();
  const editTitle = !!(location.state as { editTitle?: boolean } | null)
    ?.editTitle;
  const { me } = useMe();
  const close = () => navigate(`/boards/${boardId}`);
  const qc = useQueryClient();
  const cardKey = useMemo(() => ["card", cardId] as const, [cardId]);
  const boardKey = useMemo(() => ["board", boardId] as const, [boardId]);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: cardKey });
    qc.invalidateQueries({ queryKey: boardKey });
  };

  const { data: card, isLoading } = useQuery({
    queryKey: cardKey,
    queryFn: () => api.cards.get(cardId!),
    enabled: !!cardId,
    // poll so collaborators' comments/edits show up while the modal is open
    refetchInterval: 10_000,
  });
  // board detail is used for the link card picker (all cards on the board)
  const { data: board } = useQuery({
    queryKey: boardKey,
    queryFn: () => api.boards.get(boardId!),
    enabled: !!boardId,
  });

  const updateCard = useMutation({
    mutationFn: (patch: Parameters<typeof api.cards.update>[1]) =>
      api.cards.update(cardId!, patch),
    onSuccess: invalidate,
  });
  const removeCard = useMutation({
    mutationFn: () => api.cards.delete(cardId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: boardKey });
      navigate(`/boards/${boardId}`);
    },
  });
  const archiveCard = useMutation({
    mutationFn: () => api.cards.archive(cardId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: boardKey });
      navigate(`/boards/${boardId}`);
    },
  });
  const unarchiveCard = useMutation({
    mutationFn: () => api.cards.unarchive(cardId!),
    onSuccess: invalidate,
  });
  const addChecklist = useMutation({
    mutationFn: (title: string) =>
      api.checklists.create(cardId!, { title }),
    onSuccess: invalidate,
  });
  const renameChecklist = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      api.checklists.update(id, { title }),
    onSuccess: invalidate,
  });
  const removeChecklist = useMutation({
    mutationFn: (id: string) => api.checklists.delete(id),
    onSuccess: invalidate,
  });
  const addItem = useMutation({
    mutationFn: ({ checklistId, content }: { checklistId: string; content: string }) =>
      api.checklists.addItem(checklistId, { content }),
    onSuccess: invalidate,
  });
  const updateItem = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Parameters<typeof api.checklists.updateItem>[1];
    }) => api.checklists.updateItem(id, patch),
    onSuccess: invalidate,
  });
  const removeItem = useMutation({
    mutationFn: (id: string) => api.checklists.deleteItem(id),
    onSuccess: invalidate,
  });
  const addLink = useMutation({
    mutationFn: ({ childCardId, relationship }: { childCardId: string; relationship: CardRelationship }) =>
      api.links.create(cardId!, { childCardId, relationship }),
    onSuccess: invalidate,
  });
  const removeLink = useMutation({
    mutationFn: (id: string) => api.links.delete(id),
    onSuccess: invalidate,
  });

  const uploader = useAttachmentUpload(boardId!, cardId!);
  const [dragging, setDragging] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  // Hoisted out of EditableText so the whole header can open the title editor.
  // `editTitle` is set when a file dropped on a list just created this card —
  // its title is a filename and almost certainly wants replacing.
  const [titleEditing, setTitleEditing] = useState(editTitle);
  const dragDepth = useRef(0);

  if (isLoading) {
    return (
      <Modal onClose={close} ariaLabel="Card details">
        <div className="p-6 text-slate-500">Loading card…</div>
      </Modal>
    );
  }
  if (!card) {
    return (
      <Modal onClose={close} ariaLabel="Card details">
        <div className="p-6 text-slate-500">Card not found.</div>
      </Modal>
    );
  }

  // flatten board cards for the picker, excluding this card
  const boardCards = board
    ? board.lists
        .flatMap((l) => l.cards)
        .filter((c) => c.id !== card.id)
        .sort((a, b) => a.title.localeCompare(b.title))
    : [];
  const existingChildIds = new Set(card.links.children.map((l) => l.childCardId));
  const linkableCards = boardCards.filter((c) => !existingChildIds.has(c.id));

  // map cardId → title for displaying relationships
  const cardTitleById = new Map<string, string>();
  if (board) {
    for (const l of board.lists) for (const c of l.cards) cardTitleById.set(c.id, c.title);
  }

  const dueValue = card.dueDate ? toDateInputValue(card.dueDate) : "";
  const canEdit = board ? board.role !== "viewer" : true;

  // The cover color tints the modal header; text/controls use the cover's
  // paired text color (dark on light-theme pastels, pale on dark-theme fills).
  const cover = card.coverColor ? labelColor(card.coverColor) : null;
  const titleColor = cover ? cover.text : "text-slate-900";
  const ctrlClass = cover
    ? `${cover.text} opacity-75 hover:opacity-100 hover:bg-black/10`
    : "text-slate-400 hover:bg-slate-100 hover:text-slate-700";

  // Drag-and-drop file upload onto the whole card (editors only).
  const dragHasFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer?.types ?? []).includes("Files");
  function onDragEnter(e: React.DragEvent) {
    if (!canEdit || !dragHasFiles(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  }
  function onDragOver(e: React.DragEvent) {
    if (!canEdit || !dragHasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }
  function onDragLeave() {
    if (!canEdit) return;
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragging(false);
    }
  }
  function onDrop(e: React.DragEvent) {
    if (!canEdit) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (e.dataTransfer.files?.length) uploader.uploadFiles(e.dataTransfer.files);
  }

  return (
    <Modal onClose={close} ariaLabel="Card details" size="2xl" fitHeight>
      <div
        className="relative flex min-h-0 flex-1 flex-col"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
      {(dragging || uploader.isUploading) && canEdit && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-blue-400 bg-blue-500/10">
          <div className="rounded-lg bg-[var(--surface)] px-4 py-2 text-sm font-medium text-blue-600 shadow">
            {uploader.isUploading ? "Uploading…" : "Drop files to attach"}
          </div>
        </div>
      )}
      {/* Header (fixed) — tinted by the cover color when set.
          The whole header is the title's click target, not just the text: a
          short title left most of the bar dead, which reads as "not editable"
          rather than "aim more carefully". Clicks on the action buttons are
          excluded via the data attribute below. */}
      <div
        onClick={(e) => {
          if (!canEdit || titleEditing) return;
          if ((e.target as HTMLElement).closest("[data-header-controls]")) return;
          setTitleEditing(true);
        }}
        className={`flex shrink-0 items-center justify-between gap-3 px-5 py-3 ${
          canEdit && !titleEditing ? "cursor-text" : ""
        } ${cover ? cover.cover : "border-b border-slate-200"}`}
      >
        <h1 className={`flex-1 text-xl font-semibold ${titleColor}`}>
          {canEdit ? (
            <EditableText
              value={card.title}
              onCommit={(v) => updateCard.mutate({ title: v })}
              displayClassName={`block font-semibold ${titleColor}`}
              ariaLabel="Card title"
              maxLength={CARD_TITLE_MAX_LENGTH}
              editing={titleEditing}
              onEditingChange={setTitleEditing}
            />
          ) : (
            card.title
          )}
        </h1>
        <div data-header-controls className="flex items-center gap-1">
          {canEdit && (
            <OverflowMenu
              ariaLabel="Card actions"
              triggerClassName={`rounded p-1.5 leading-none ${ctrlClass}`}
            >
              <button
                type="button"
                onClick={() => setMoveOpen(true)}
                className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100"
              >
                Move card…
              </button>
              {!card.archivedAt && (
                <button
                  type="button"
                  onClick={() => archiveCard.mutate()}
                  className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100"
                >
                  Archive card
                </button>
              )}
              <ConfirmButton
                onConfirm={() => removeCard.mutate()}
                title="Delete card"
                className="block w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                confirmClassName="block w-full px-3 py-1.5 text-left text-sm font-medium text-red-700 bg-red-50"
                confirmLabel="Click again to delete"
              >
                Delete card
              </ConfirmButton>
            </OverflowMenu>
          )}
          <button
            type="button"
            onClick={close}
            title="Close"
            aria-label="Close"
            className={`rounded p-1.5 ${ctrlClass}`}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Archived banner */}
      {card.archivedAt && (
        <div className="flex shrink-0 items-center justify-between gap-3 bg-amber-50 px-5 py-2 text-sm text-amber-800">
          <span>This card is archived — it isn't shown on the board.</span>
          {canEdit && (
            <button
              type="button"
              onClick={() => unarchiveCard.mutate()}
              className="rounded bg-amber-100 px-2.5 py-1 font-medium hover:bg-amber-200"
            >
              Restore
            </button>
          )}
        </div>
      )}

      {/* Body: main content (left, scrolls) + activity (right, scrolls) */}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div className="themed-scroll flex-1 space-y-5 overflow-y-auto p-5">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-slate-500">Description</span>
              <DescriptionByline activity={card.activity} />
            </div>
          <div className="mt-1">
            <MarkdownField
              value={card.description ?? ""}
              placeholder={canEdit ? "Add a description…" : "No description."}
              canEdit={canEdit}
              onCommit={(v) => updateCard.mutate({ description: v || null })}
              onPasteFiles={(files) => uploader.uploadFiles(files)}
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Due date</span>
            <input
              type="date"
              value={dueValue}
              disabled={!canEdit}
              onChange={(e) => {
                const v = e.target.value;
                updateCard.mutate({
                  dueDate: v ? fromDateInputValue(v) : null,
                });
              }}
              className="rounded border border-slate-300 px-2 py-1 text-sm disabled:opacity-60"
            />
            {card.dueDate && canEdit && (
              <button
                type="button"
                onClick={() => updateCard.mutate({ dueDate: null })}
                className="text-xs text-slate-400 hover:text-red-600"
              >
                clear
              </button>
            )}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={card.completed}
              disabled={!canEdit}
              onChange={(e) => updateCard.mutate({ completed: e.target.checked })}
            />
            <span className="text-slate-700">Completed</span>
          </label>
        </div>

        {/* Cover */}
        {canEdit && (
          <div className="mt-3">
            <span className="text-xs text-slate-500">Cover</span>
            <div className="mt-1">
              <ColorSwatches
                value={card.coverColor}
                onChange={(coverColor) => updateCard.mutate({ coverColor })}
                allowNone
              />
            </div>
          </div>
        )}

        {/* Assignees */}
        <CardAssignees
          boardId={boardId!}
          cardId={card.id}
          assigned={card.assignees}
          canEdit={canEdit}
        />

        {/* Labels */}
        <CardLabels
          boardId={boardId!}
          cardId={card.id}
          assigned={card.labels}
          canEdit={canEdit}
        />

        {/* Custom fields */}
        <CardFields
          boardId={boardId!}
          cardId={card.id}
          fields={[...(board?.fields ?? [])].sort((a, b) => a.position - b.position)}
          values={card.fieldValues ?? {}}
          canEdit={canEdit}
          onManage={() => setFieldsOpen(true)}
        />
        {fieldsOpen && (
          <BoardFieldsDialog boardId={boardId!} onClose={() => setFieldsOpen(false)} />
        )}

        {/* Checklists */}
        <CollapsibleSection
          title="Checklists"
          action={
            canEdit ? (
              <button
                type="button"
                onClick={() => addChecklist.mutate("Checklist")}
                className="rounded bg-slate-100 px-2 py-1 text-sm text-slate-700 hover:bg-slate-200"
              >
                + Add checklist
              </button>
            ) : undefined
          }
        >
        {card.checklists.length === 0 ? (
          <p className="text-sm text-slate-500">No checklists yet.</p>
        ) : (
          <ul className="space-y-3">
            {card.checklists.map((cl) => {
              const done = cl.items.filter((i) => i.checked).length;
              const pct =
                cl.items.length === 0 ? 0 : Math.round((done / cl.items.length) * 100);
              return (
                <li
                  key={cl.id}
                  className="rounded-lg border border-slate-200 bg-[var(--surface)] p-4"
                >
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="flex-1 font-medium">
                      {canEdit ? (
                        <EditableText
                          value={cl.title}
                          onCommit={(v) => renameChecklist.mutate({ id: cl.id, title: v })}
                          displayClassName="font-medium text-slate-800"
                          ariaLabel="Checklist title"
                        />
                      ) : (
                        <span className="font-medium text-slate-800">{cl.title}</span>
                      )}
                    </h3>
                    <span className="text-xs text-slate-500">
                      {done}/{cl.items.length}
                    </span>
                    {canEdit && (
                      <ConfirmButton
                        onConfirm={() => removeChecklist.mutate(cl.id)}
                        title="Delete checklist"
                        className="rounded px-1.5 py-1 text-xs text-slate-400 hover:bg-slate-100 hover:text-red-600"
                        confirmClassName="rounded px-1.5 text-xs bg-red-100 text-red-700"
                        confirmLabel="Delete?"
                      >
                        <X size={14} />
                      </ConfirmButton>
                    )}
                  </div>

                  <div className="mt-2 h-1.5 w-full rounded bg-slate-100">
                    <div
                      className="h-1.5 rounded bg-green-500 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  <ul className="mt-2 space-y-1">
                    {cl.items.map((item) => (
                      <li
                        key={item.id}
                        className="group flex items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={item.checked}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateItem.mutate({
                              id: item.id,
                              patch: { checked: e.target.checked },
                            })
                          }
                        />
                        <span
                          className={
                            item.checked ? "flex-1 text-slate-400 line-through" : "flex-1"
                          }
                        >
                          {canEdit ? (
                            <EditableText
                              value={item.content}
                              onCommit={(v) =>
                                updateItem.mutate({ id: item.id, patch: { content: v } })
                              }
                              displayClassName="text-slate-700"
                              ariaLabel="Item content"
                            />
                          ) : (
                            <span className="text-slate-700">{item.content}</span>
                          )}
                        </span>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => removeItem.mutate(item.id)}
                            className="text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-600"
                            title="Delete item"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>

                  {canEdit && (
                    <AddItem
                      onAdd={(content) => addItem.mutate({ checklistId: cl.id, content })}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
        </CollapsibleSection>

        {/* Attachments */}
        <CardAttachments
          boardId={boardId!}
          cardId={card.id}
          attachments={card.attachments}
          canEdit={canEdit}
          uploader={uploader}
        />

        {/* Relationships */}
        <CollapsibleSection title="Relationships" defaultOpen={false}>
        <div className="rounded-lg border border-slate-200 bg-[var(--surface)] p-4">
          {/* parents */}
          {card.links.parents.length > 0 && (
            <div className="mb-3">
              <div className="text-xs text-slate-500">Parents</div>
              <ul className="mt-1 space-y-1 text-sm">
                {card.links.parents.map((l) => (
                  <li key={l.id} className="flex items-center gap-2">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                      {l.relationship}
                    </span>
                    <Link
                      to={`/boards/${boardId}/cards/${l.parentCardId}`}
                      className="text-blue-600 hover:underline"
                    >
                      {cardTitleById.get(l.parentCardId) ?? l.parentCardId.slice(0, 8)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* children */}
          {card.links.children.length > 0 && (
            <div className="mb-3">
              <div className="text-xs text-slate-500">Children</div>
              <ul className="mt-1 space-y-1 text-sm">
                {card.links.children.map((l) => (
                  <li key={l.id} className="flex items-center gap-2">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                      {l.relationship}
                    </span>
                    <Link
                      to={`/boards/${boardId}/cards/${l.childCardId}`}
                      className="flex-1 text-blue-600 hover:underline"
                    >
                      {cardTitleById.get(l.childCardId) ?? l.childCardId.slice(0, 8)}
                    </Link>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => removeLink.mutate(l.id)}
                        className="text-slate-400 hover:text-red-600"
                        title="Remove link"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {card.links.parents.length === 0 && card.links.children.length === 0 && (
            <p className="text-sm text-slate-500">No card relationships.</p>
          )}

          {/* add child link */}
          {canEdit && linkableCards.length > 0 && (
            <AddLink onAdd={(childCardId, relationship) => addLink.mutate({ childCardId, relationship })} cards={linkableCards} />
          )}
        </div>
        </CollapsibleSection>
        </div>

        {/* Activity & comments (right column) */}
        <div className="themed-scroll shrink-0 overflow-y-auto border-t border-slate-200 p-5 lg:w-[32rem] lg:border-l lg:border-t-0">
          <CardActivity
            cardId={card.id}
            boardId={boardId!}
            comments={card.comments}
            activity={card.activity}
            canEdit={canEdit}
            currentUserId={me?.id}
            onPasteFiles={(files) => uploader.uploadFiles(files)}
          />
        </div>
      </div>
      </div>

      {moveOpen && (
        <MoveCardDialog
          cardId={card.id}
          currentBoardId={boardId!}
          currentListId={card.listId}
          onClose={() => setMoveOpen(false)}
        />
      )}
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */

function AddItem({ onAdd }: { onAdd: (content: string) => void }) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");

  function submit() {
    const v = content.trim();
    if (!v) {
      setOpen(false);
      return;
    }
    onAdd(v);
    setContent("");
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 text-sm text-slate-500 hover:text-slate-700"
      >
        + Add item
      </button>
    );
  }

  return (
    <div className="mt-2 flex gap-2">
      <input
        autoFocus
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          } else if (e.key === "Escape") {
            setOpen(false);
            setContent("");
          }
        }}
        onBlur={() => {
          submit();
          setOpen(false);
        }}
        placeholder="Item content"
        className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm outline-none focus:border-blue-400"
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function AddLink({
  onAdd,
  cards,
}: {
  onAdd: (childCardId: string, relationship: CardRelationship) => void;
  cards: { id: string; title: string }[];
}) {
  const [childId, setChildId] = useState("");
  const [relationship, setRelationship] = useState<CardRelationship>("subtask");

  function submit() {
    if (!childId) return;
    onAdd(childId, relationship);
    setChildId("");
    setRelationship("subtask");
  }

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <div className="text-xs text-slate-500">Add child link</div>
      <div className="mt-1 flex flex-wrap gap-2">
        <select
          value={childId}
          onChange={(e) => setChildId(e.target.value)}
          className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
        >
          <option value="">Select a card…</option>
          {cards.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
        <select
          value={relationship}
          onChange={(e) => setRelationship(e.target.value as CardRelationship)}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        >
          {RELATIONSHIPS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={submit}
          disabled={!childId}
          className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Link
        </button>
      </div>
    </div>
  );
}
/* -------------------------------------------------------------------------- */

/**
 * Who last wrote the description, and when.
 *
 * The description is where a card's actual requirements live, and on a shared
 * board it may not have been written by the person who asked you to act on it.
 * Until now a rewrite left no trace anywhere; this is the trace, put where a
 * reader is already looking rather than buried in the activity list.
 */
function DescriptionByline({ activity }: { activity: ActivityView[] }) {
  const last = [...activity]
    .filter((a) => a.type === "described" || a.type === "created")
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0];
  if (!last || last.type === "created") return null;

  const who = last.actorDisplayName ?? last.actorUsername ?? "someone";
  return (
    <span className="text-xs text-slate-400">
      edited by {who} {formatRelativeTime(last.createdAt)}
    </span>
  );
}
